import React, { useState, useMemo } from 'react';
import { Task, TaskStage } from '../../../types/Task';
import { Project } from '../../../types/Project';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from '@/components/ui';

interface TaskChangeInfo {
  id: number;
  name: string;
  type: string;
  currentStatus: string;
  newStatus: string;
  plannedAccomplishment?: number;
  gpsCoordinates?: string;
  completionDate?: string;
  changes: string[];
  hasEffort: boolean; // New field to track if task has effort
}

interface CloseAllTasksModalProps {
  visible: boolean;
  project: Project | null;
  onClose: () => void;
  onConfirm: (comments: string, defaultCompletionDate: string, quantifiedTaskAction: 'INTERNAL_REVIEW' | 'CLOSED') => void;
  loading?: boolean;
}

const CloseAllTasksModal: React.FC<CloseAllTasksModalProps> = ({
  visible,
  project,
  onClose,
  onConfirm,
  loading = false
}) => {
  const [comments, setComments] = useState('');
  const [defaultCompletionDate, setDefaultCompletionDate] = useState('');
  const [quantifiedTaskAction, setQuantifiedTaskAction] = useState<'INTERNAL_REVIEW' | 'CLOSED'>('INTERNAL_REVIEW');

  const taskChanges = useMemo(() => {
    if (!project?.tasks) return [];

    const changes: TaskChangeInfo[] = [];
    
    project.tasks.forEach(task => {
      if (task.status === TaskStage.CLOSED || task.status === TaskStage.CANCELLED) {
        return; // Skip already closed or cancelled tasks
      }

      // Check if task has effort to determine completion date
      const hasEffort = Boolean(task.efforts && task.efforts.length > 0 && 
        task.efforts.some(effort => effort.date));

      // Set completion date based on priority logic:
      // 1. If there's already a Completion Date set, use it
      // 2. If not, try to use the latest Effort date on the task
      // 3. If no effort date, then use the Default date set in the box
      let completionDate: string;
      
      if (task.completionDate) {
        // Priority 1: Use existing completion date
        completionDate = new Date(task.completionDate).toISOString().split('T')[0];
      } else if (hasEffort) {
        // Priority 2: Find the latest effort date
        const effortDates = task.efforts!
          .map(effort => effort.date)
          .filter(date => date) // Filter out undefined/null dates
          .map(date => new Date(date!));
        
        if (effortDates.length > 0) {
          const latestEffortDate = new Date(Math.max(...effortDates.map(d => d.getTime())));
          completionDate = latestEffortDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        } else {
          // No effort dates found, use default date (must be provided)
          completionDate = defaultCompletionDate;
        }
      } else {
        // Priority 3: No existing date and no effort - use default completion date (must be provided)
        completionDate = defaultCompletionDate;
      }

      const changeInfo: TaskChangeInfo = {
        id: task.id,
        name: task.name || task.description || 'Unnamed Task',
        type: task.type,
        currentStatus: task.status,
        newStatus: '',
        completionDate,
        changes: [],
        hasEffort
      };

      if (task.type === 'Quantified') {
        changeInfo.newStatus = quantifiedTaskAction;
        changeInfo.changes.push(`Status: ${quantifiedTaskAction}`);
        changeInfo.changes.push(`Completion Date: ${completionDate}`);
        
        // Get planned accomplishment from quantified tasks
        const plannedQuantity = task.quantifiedTasks?.[0]?.identifiedQuantity;
        if (plannedQuantity) {
          changeInfo.plannedAccomplishment = plannedQuantity;
          changeInfo.changes.push(`Completion Accomplishment: ${plannedQuantity}`);
        }

        // Get GPS coordinates
        const startLocation = task.taskLocations?.find(loc => loc.type === 'START');
        const endLocation = task.taskLocations?.find(loc => loc.type === 'END');
        
        let gpsText = '';
        if (startLocation) {
          gpsText += `Start: (${startLocation.latitude}, ${startLocation.longitude})`;
        }
        if (endLocation) {
          if (gpsText) gpsText += ', ';
          gpsText += `End: (${endLocation.latitude}, ${endLocation.longitude})`;
        }
        if (gpsText) {
          changeInfo.gpsCoordinates = gpsText;
        }
      } else {
        changeInfo.newStatus = TaskStage.CLOSED;
        changeInfo.changes.push('Status: CLOSED');
        changeInfo.changes.push(`Completion Date: ${completionDate}`);
      }

      changes.push(changeInfo);
    });

    return changes;
  }, [project, defaultCompletionDate, quantifiedTaskAction]);

  const quantifiedTasks = taskChanges.filter(task => task.type === 'Quantified');
  const nonQuantifiedTasks = taskChanges.filter(task => task.type !== 'Quantified');
  const tasksWithoutEffort = taskChanges.filter(task => !task.hasEffort);

  const handleConfirm = () => {
    // Validate that default completion date is provided
    if (!defaultCompletionDate) {
      alert('Please enter a Default Completion Date before proceeding.');
      return;
    }
    
    // Check if there are tasks without effort and require comments
    const tasksWithoutEffort = taskChanges.filter(task => !task.hasEffort);
    if (tasksWithoutEffort.length > 0 && !comments.trim()) {
      alert(`Comments are required when closing ${tasksWithoutEffort.length} task(s) without any efforts. Please add comments explaining why no efforts were recorded.`);
      return;
    }
    
    onConfirm(comments, defaultCompletionDate, quantifiedTaskAction);
  };

  return (
    <Modal show={visible} onHide={onClose} size="lg" backdrop={loading ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-lock tw-mr-2"></i>
          Close All Tasks - {project?.name}
        </Modal.Title>
      </Modal.Header>

          <Modal.Body>
            {taskChanges.length === 0 ? (
              <div className="tw-text-center tw-py-4">
                <i className="bi bi-check-circle-fill tw-text-success tw-text-5xl"></i>
                <h6 className="tw-mt-3">No tasks to close</h6>
                <p className="tw-text-content-muted">All tasks are already closed or cancelled.</p>
              </div>
            ) : (
              <>
                <Alert variant="warning" className="tw-mb-3">
                  <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                  <strong>Warning:</strong> This action will affect {taskChanges.length} task(s).
                  Please review the changes below before proceeding.
                </Alert>

                {/* Warning for tasks without effort */}
                {tasksWithoutEffort.length > 0 && (
                  <Alert variant="danger" className="tw-mb-3">
                    <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                    <strong>Warning:</strong> {tasksWithoutEffort.length} task(s) have no efforts recorded.
                    Comments are required to explain why no efforts were recorded before closing these tasks.
                  </Alert>
                )}

                {/* Default Completion Date Field */}
                <div className="tw-mb-4">
                  <Form.Label htmlFor="defaultCompletionDate">
                    <strong>Default Completion Date <span className="tw-text-danger">*</span></strong>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    id="defaultCompletionDate"
                    value={defaultCompletionDate}
                    onChange={(e) => setDefaultCompletionDate(e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>

                {/* Quantified Task Action Selection */}
                {quantifiedTasks.length > 0 && (
                  <div className="tw-mb-4">
                    <Form.Label>
                      <strong>Quantified Tasks Action <span className="tw-text-danger">*</span></strong>
                    </Form.Label>
                    <Row>
                      <Col md={6}>
                        <Form.Check
                          type="radio"
                          name="quantifiedTaskAction"
                          id="internalReview"
                          value="INTERNAL_REVIEW"
                          checked={quantifiedTaskAction === 'INTERNAL_REVIEW'}
                          onChange={(e) => setQuantifiedTaskAction(e.target.value as 'INTERNAL_REVIEW' | 'CLOSED')}
                          disabled={loading}
                          label="Move to Internal Review"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          type="radio"
                          name="quantifiedTaskAction"
                          id="closed"
                          value="CLOSED"
                          checked={quantifiedTaskAction === 'CLOSED'}
                          onChange={(e) => setQuantifiedTaskAction(e.target.value as 'INTERNAL_REVIEW' | 'CLOSED')}
                          disabled={loading}
                          label="Close Tasks"
                        />
                      </Col>
                    </Row>
                    <Form.Text>
                      <i className="bi bi-info-circle tw-mr-1"></i>
                      This will affect {quantifiedTasks.length} quantified task(s) in this project.
                    </Form.Text>
                  </div>
                )}

                <div className="tw-mb-4">
                  <Form.Label htmlFor="comments">
                    <strong>
                      Comments
                      {tasksWithoutEffort.length > 0 && <span className="tw-text-danger tw-ml-1">*</span>}
                      {tasksWithoutEffort.length === 0 && ' (Optional)'}
                    </strong>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    id="comments"
                    rows={3}
                    placeholder={tasksWithoutEffort.length > 0 ?
                      `Comments are required when closing ${tasksWithoutEffort.length} task(s) without efforts. Please explain why no efforts were recorded...` :
                      "Enter any comments about closing all tasks..."
                    }
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    disabled={loading}
                    required={tasksWithoutEffort.length > 0}
                  />
                  {tasksWithoutEffort.length > 0 && (
                    <Form.Text>
                      <small>Comments are mandatory when closing tasks without any efforts.</small>
                    </Form.Text>
                  )}
                </div>

                {/* Summary Stats */}
                <Row className="tw-mb-4">
                  <Col md={6}>
                    <Card className="tw-overflow-hidden">
                      <Card.Body className="tw-bg-[#cff4fc] tw-text-center">
                        <h5 className="tw-mb-2 tw-text-info">
                          {quantifiedTasks.length}
                        </h5>
                        <p className="tw-mb-0">Quantified Tasks → {quantifiedTaskAction === 'INTERNAL_REVIEW' ? 'Internal Review' : 'Closed'}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="tw-overflow-hidden">
                      <Card.Body className="tw-bg-[#e2e3e5] tw-text-center">
                        <h5 className="tw-mb-2 tw-text-secondary">
                          {nonQuantifiedTasks.length}
                        </h5>
                        <p className="tw-mb-0">Other Tasks → Closed</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Quantified Tasks Section */}
                {quantifiedTasks.length > 0 && (
                  <div className="tw-mb-4">
                    <h6 className="tw-text-black">
                      <i className="bi bi-clipboard-data tw-mr-2"></i>
                      Quantified Tasks (Set to {quantifiedTaskAction === 'INTERNAL_REVIEW' ? 'Internal Review' : 'Closed'})
                    </h6>
                    <div className="tw-w-full tw-overflow-x-auto">
                      <Table size="sm" bordered>
                        <thead className="tw-bg-surface">
                          <tr>
                            <th>Task Name</th>
                            <th>Current Status</th>
                            <th>New Status</th>
                            <th>Planned Accomplishment</th>
                            <th>Completion Date</th>
                            <th>GPS Coordinates</th>
                            <th>Has Effort</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quantifiedTasks.map(task => (
                            <tr key={task.id}>
                              <td className="tw-break-words" style={{ maxWidth: '200px' }}>
                                {task.name}
                              </td>
                              <td>
                                <Badge bg="warning">{task.currentStatus}</Badge>
                              </td>
                              <td>
                                <Badge bg={quantifiedTaskAction === 'INTERNAL_REVIEW' ? 'info' : 'secondary'}>
                                  {quantifiedTaskAction}
                                </Badge>
                              </td>
                              <td className="tw-text-center">
                                {task.plannedAccomplishment ?? 'N/A'}
                              </td>
                              <td className="tw-text-center">
                                {task.completionDate}
                              </td>
                              <td className="tw-text-sm tw-text-content-muted">
                                {task.gpsCoordinates || 'No coordinates'}
                              </td>
                              <td className="tw-text-center">
                                <Badge bg={task.hasEffort ? 'success' : 'warning'}>
                                  {task.hasEffort ? 'Yes' : 'No'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Non-Quantified Tasks Section */}
                {nonQuantifiedTasks.length > 0 && (
                  <div className="tw-mb-4">
                    <h6 className="tw-text-secondary">
                      <i className="bi bi-list-task tw-mr-2"></i>
                      Other Tasks (Set to Closed)
                    </h6>
                    <div className="tw-w-full tw-overflow-x-auto">
                      <Table size="sm" bordered>
                        <thead className="tw-bg-surface">
                          <tr>
                            <th>Task Name</th>
                            <th>Type</th>
                            <th>Current Status</th>
                            <th>New Status</th>
                            <th>Completion Date</th>
                            <th>Has Effort</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nonQuantifiedTasks.map(task => (
                            <tr key={task.id}>
                              <td className="tw-break-words" style={{ maxWidth: '250px' }}>
                                {task.name}
                              </td>
                              <td>
                                <span className={`tw-inline-block tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none ${
                                  task.type === 'Routine' ? 'tw-bg-warning tw-text-warning-text' :
                                  task.type === 'Overhead' ? 'tw-bg-[#ced4da] tw-text-black' :
                                  'tw-bg-[#cfe2ff] tw-text-primary'
                                }`}>
                                  {task.type}
                                </span>
                              </td>
                              <td>
                                <Badge bg="warning">{task.currentStatus}</Badge>
                              </td>
                              <td>
                                <Badge bg="secondary">CLOSED</Badge>
                              </td>
                              <td className="tw-text-center">
                                {task.completionDate}
                              </td>
                              <td className="tw-text-center">
                                <Badge bg={task.hasEffort ? 'success' : 'warning'}>
                                  {task.hasEffort ? 'Yes' : 'No'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            {taskChanges.length > 0 && (
              <Button
                variant="warning"
                onClick={handleConfirm}
                disabled={loading || (tasksWithoutEffort.length > 0 && !comments.trim())}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="tw-mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-lock tw-mr-2"></i>
                    Close All Tasks ({taskChanges.length})
                  </>
                )}
              </Button>
            )}
          </Modal.Footer>
    </Modal>
  );
};

export default CloseAllTasksModal;